<template>
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="isOpen"
        class="fixed inset-0 flex items-center justify-center backdrop-blur-md bg-black/30 z-50"
      >
        <div
          class="flex flex-col md:flex-row items-center justify-center bg-white rounded-lg shadow-lg p-6 gap-6 w-full max-w-xl mx-4 md:mx-0"
          :class="customClass"
        >
          <!-- Columna izquierda -->
          <div class="flex flex-col items-center gap-4 w-full md:w-1/2">
            <!-- Imagen de fondo -->
            <ImageBackground v-if="!selectedFile && !previewImage" />

            <!-- Imagen seleccionada -->
            <img
              v-if="previewImage"
              :src="previewImage"
              alt="Previsualización"
              class="w-full h-48 object-cover rounded-lg border border-gray-300"
            />

            <!-- Input de imagen -->
            <input
              ref="fileInput"
              type="file"
              :accept="accept"
              @change="handleFileChange"
              class="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4
                     file:rounded-lg file:border-0 file:text-sm file:font-semibold
                     file:bg-blue-50 file:text-blue-500 hover:file:bg-blue-100"
            />

            <!-- Botón quitar -->
            <button
              v-if="previewImage"
              @click="removeImage"
              class="text-sm text-red-400 hover:underline"
            >
              {{ $t('modal.remove') }}
            </button>
          </div>

          <!-- Columna derecha -->
          <div class="flex flex-col items-center justify-center gap-1.5 w-full md:w-1/2">
            <!-- Icono -->
            <div v-if="icon" class="flex justify-center mb-3">
              <component :is="icon" class="w-12 h-12 text-blue-300" />
            </div>

            <!-- Título -->
            <h2 class="text-xl font-semibold text-gray-800 text-center">
              {{ title }}
            </h2>

            <!-- Texto -->
            <p class="text-gray-600 text-center px-4">
              {{ text }}
            </p>

            <!-- Botones -->
            <div class="flex flex-wrap justify-center gap-3">
              <button
                v-for="(btn, index) in buttons"
                :key="index"
                @click="() => btn.action(previewImage)"
                :class="btn.class"
                class="px-4 py-2 rounded-lg transition-all duration-300"
              >
                {{ btn.text }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref,  onUnmounted } from 'vue'
import ImageBackground from '@/assets/svg/ImageBackground.vue'

defineProps({
  isOpen: Boolean,
  title: String,
  text: String,
  buttons: Array,
  icon: [Object, Function],
  customClass: String,
  src: [String, File],
  // Formatos que admite quien abre el modal; por defecto, cualquier imagen.
  accept: { type: String, default: 'image/*' }
})

const emit = defineEmits(['update:src'])
const fileInput = ref(null)
const selectedFile = ref(null)
const previewImage = ref(null)

const handleFileChange = (e) => {
  const file = e.target.files[0]
  if (file) {
    selectedFile.value = file
    const reader = new FileReader()

    reader.onload = (e) => {
      previewImage.value = e.target.result
      emit('update:src', file)
    }

    reader.readAsDataURL(file)
  }
}

const removeImage = () => {
  if (fileInput.value) {
    fileInput.value.value = '' // Limpiar el input file
  }
  selectedFile.value = null
  previewImage.value = null
  emit('update:src', null)
}

// Limpiar cuando el componente se desmonta
onUnmounted(() => {
  if (previewImage.value) {
    previewImage.value = null
  }
  if (selectedFile.value) {
    selectedFile.value = null
  }
})

</script>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
